import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getUserRoleReq } from '../../service/branchService';
import { createRoleReq, getModuleListReq, updateRoleReq } from '../../service/usersService';
import { setBreadcrumbItems } from '../../store/actions';



const RoleDetails = ({ setBreadcrumbItems }) => {
  const location = useLocation();
  const { role, action } = useParams();
  const navigate = useNavigate()

  const safeAction = (location.state?.action || action || 'view').toLowerCase();
  const isViewMode = safeAction === 'view';
  const isCreateMode = safeAction === 'create';

  const [roleTitle, setRoleTitle] = useState(role || '');
  const [userType, setUserType] = useState('client');
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allModules, setAllModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  const breadcrumbItems = [
    { title: 'Dashboard', link: '/dashboard' },
    { title: 'Roles & Permissions', link: '/roles-permissions' },
    { title: role, link: '#' },
  ];

   const user = JSON.parse(localStorage.getItem('user'));
   const clientId = user.clientId
  useEffect(() => {
    setBreadcrumbItems(
      `${safeAction.charAt(0).toUpperCase() + safeAction.slice(1)} Role`,
      breadcrumbItems
    );
  }, [role, safeAction, setBreadcrumbItems]);

  useEffect(() => {
    const fetchRoleDetails = async () => {
      try {
        if (isCreateMode) {
          setLoading(false);
          return;
        }

        const isAdmin = location.state?.isAdmin ?? false;
        const res = await getUserRoleReq(isAdmin);
        const roles = res?.payload?.roles || [];

        const foundRole = roles.find(
          (r) => r.title?.toLowerCase() === decodeURIComponent(role).toLowerCase()
        );

        if (foundRole) {
          setRoleTitle(foundRole.title);
          setUserType(foundRole.userType);
          const formattedModules = foundRole.permissions.map((perm) => ({
            name: perm.module,
            operations: Array.isArray(perm.operations) ? perm.operations : [],
          }));
          setModules(formattedModules);
        } else {
          setRoleTitle('Unknown Role');
        }
      } catch (err) {
        console.error('Error fetching role details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoleDetails();
  }, [role, isCreateMode, location.state]);

  useEffect(() => {
    const fetchAllModules = async () => {
      try {
        const res = await getModuleListReq();
        const list = res?.payload?.modules || [];
        setAllModules(list);
      } catch (err) {
        console.error('Error fetching module list:', err);
      }
    };
    fetchAllModules();
  }, []);

  const handlePermissionToggle = (moduleName, operation) => {
    setModules((prev) => {
      const updated = prev.map((module) => {
        if (module.name === moduleName) {
          const newOps = module.operations.includes(operation)
            ? module.operations.filter((op) => op !== operation)
            : [...module.operations, operation];
          return { ...module, operations: newOps };
        }
        return module;
      });
      return updated;
    });
  };


  const handleCreateOrUpdate = async () => {

    if (!clientId || !user?._id) {
      toast('Please try again later', { type: 'error' });
      return;
    }

    const payload = {
      title: roleTitle,
      userType,
      permissions: modules.map((module) => ({
        module: module.name,
        operations: module.operations,
      })),
      clientId,
      createdBy: user._id,
      updatedBy: user._id,
    };

    try {
      if (isCreateMode) {
        await createRoleReq(payload);
        toast('Role created successfully!', { type: 'success' });
      } else {
        const roleId = location.state.role._id;
        await updateRoleReq(roleId, payload);
        toast('Role updated successfully!', { type: 'success' });
      }
    } catch (error) {
      console.error('Error saving role:', error);
      toast('Error saving role!', { type: 'error' });
    }
    finally {
      navigate('/roles-permissions')
    }
  };


  const handleAddModuleClick = () => {
    const existingNames = modules.map((m) => m.name.toLowerCase());
    const filtered = allModules.filter(
      (moduleName) => !existingNames.includes(moduleName.toLowerCase())
    );
    setAvailableModules(filtered);
    setSelectedModules([]);
    setOpenDialog(true);
  };

  const handleConfirmAddModules = () => {
    if (selectedModules.length === 0) {
      setOpenDialog(false);
      return;
    }

    const newModules = selectedModules.map((name) => ({
      name,
      operations: [],
    }));

    setModules((prev) => [...prev, ...newModules]);
    setOpenDialog(false);
  };

  const handleDeleteModule = (moduleName) => {
    setModules(prev => prev.filter(module => module.name !== moduleName));
  };
  if (loading)
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='50vh'>
        <CircularProgress />
      </Box>
    );

  return (
    <Box p={3}>
      {/* Header Buttons */}
      <Box display='flex' justifyContent='flex-end' gap={2} mb={3}>
        {!isViewMode && (
          <Button variant='contained' color='primary' onClick={handleCreateOrUpdate}>
            {isCreateMode ? 'Create' : 'Save'}
          </Button>
        )}
        <Button variant='outlined' color='secondary' onClick={() => window.history.back()}>
          {isViewMode ? 'Back' : 'Cancel'}
        </Button>
      </Box>

      {/* Role Info */}
      <Box display='flex' gap={4} mb={4} flexDirection={{ xs: 'column', md: 'row' }}>
        <TextField
          label='Title'
          value={roleTitle}
          onChange={(e) => setRoleTitle(e.target.value)}
          fullWidth
          disabled={isViewMode}
        />
        <Select
          value={userType}
          onChange={(e) => setUserType(e.target.value)}
          fullWidth
          disabled={isViewMode}
        >
          {user?.userType === "admin" && (
            <MenuItem value="admin">Admin</MenuItem>
          )}

          <MenuItem value="client">Client</MenuItem>
        </Select>
      </Box>

      {/* Modules Table */}
      <Typography variant='h6' mb={2}>
        Modules & Permissions
      </Typography>
      <Box border={1} borderColor='#ddd' borderRadius={2} p={2}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa' }}>
            <tr>
              <th style={{ padding: '8px', textAlign: 'left' }}>Module</th>
              {['create', 'read', 'update', 'delete','approve'].map((op) => (
                <th key={op} style={{ padding: '8px', textAlign: 'center' }}>
                  {op.charAt(0).toUpperCase() + op.slice(1)}
                </th>
              ))}
              {!isViewMode && <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {modules.length > 0 ? (
              modules.map((module) => (
                <tr key={module.name}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                    {module.name.charAt(0).toUpperCase() + module.name.slice(1)}
                  </td>
                  {['create', 'read', 'update', 'delete','approve'].map((op) => {
                    const isChecked = module.operations.includes(op);
                    return (
                      <td
                        key={op}
                        style={{
                          textAlign: 'center',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onClick={() => !isViewMode && handlePermissionToggle(module.name, op)}
                          disabled={isViewMode}
                          sx={{
                            p: 0,
                            color: isChecked ? '#1976d2' : '#90caf9',
                            '&.Mui-checked': {
                              color: '#1976d2',
                            },
                            '&.Mui-disabled': {
                              color: '#90caf9',
                            },
                          }}
                        />
                      </td>
                    );
                  })}
                  {!isViewMode && (
                    <td style={{ textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <Button
                        variant='outlined'
                        color='error'
                        size='small'
                        onClick={() => handleDeleteModule(module.name)}
                      >
                        Delete
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>
                  No modules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>


      {/* Add Module Button */}
      {!isViewMode && (
        <Box display='flex' justifyContent='center' mt={3}>
          <Button variant='outlined' color='primary' onClick={handleAddModuleClick}>
            Add Module
          </Button>
        </Box>
      )}

      {/* Add Module Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Select Modules to Add</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Modules</InputLabel>
            <Select
              multiple
              value={selectedModules}
              onChange={(e) => setSelectedModules(e.target.value)}
              input={<OutlinedInput label='Modules' />}
              renderValue={(selected) => selected.join(', ')}
            >
              {availableModules.map((moduleName) => (
                <MenuItem key={moduleName} value={moduleName}>
                  <Checkbox checked={selectedModules.includes(moduleName)} />
                  <ListItemText primary={moduleName} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmAddModules} variant='contained'>
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default connect(null, { setBreadcrumbItems })(RoleDetails);
